/**
 * useStepUpChallenge — the REQ-AUTH-005 step-up sequence as a reusable hook.
 *
 * Extracted from /auth/refresh-step-up (FF-UI-2) so the same verified flow
 * drives BOTH the standalone route (pre-emptive gates, deep links) and the
 * global step-up modal (mutation replay).
 *
 * TWO factors plug into the SAME Elreign-side posture (app_metadata.mfa_challenge_at):
 *
 *   TOTP path (unchanged, Supabase MFA):
 *     1. supabase.auth.mfa.challenge + verify  — lifts aal to aal2 + amr.
 *     2. POST /api/v1/auth/refresh-step-up      — backend confirms aal2 + fresh
 *        amr and stamps app_metadata.mfa_challenge_at.
 *     3. supabase.auth.refreshSession()         — the stamp lands in a NEW token.
 *
 *   Passkey path (self-hosted WebAuthn — Supabase has no native WebAuthn MFA):
 *     1. POST /api/v1/auth/webauthn/step-up/options → startAuthentication →
 *        POST /api/v1/auth/webauthn/step-up  — backend verifies the assertion
 *        server-side and stamps the SAME app_metadata.mfa_challenge_at.
 *     2. supabase.auth.refreshSession()         — the stamp lands in a NEW token.
 *
 * The passkey branch is offered only when the browser supports WebAuthn AND the
 * user has at least one passkey enrolled (queried from Elreign's own backend —
 * NOT supabase.auth.mfa.listFactors, which never returns a WebAuthn factor). A
 * user with no passkey always falls back to TOTP — WebAuthn never causes lockout.
 *
 * `verifyTotp`/`verifyPasskey` return true on full success; on failure they set
 * `error` and return false so callers keep their UI open for a retry.
 *
 * ── The opt-in extra email code (Paul, 2026-08-18) ──────────────────────────
 *
 * A user who switched the extra email code on is challenged for an emailed code
 * BEFORE either factor is accepted here. That ordering is the safety property:
 * neither `mfa.verify` nor `stampStepUpFreshness()` runs until the email code
 * clears, so no aal2 promotion and no `mfa_challenge_at` stamp can happen on the
 * strength of an email. The email code grants NOTHING — it only decides whether
 * this hook will proceed to the thing that does. See lib/mfa-email-code.ts.
 *
 * WHY IT REUSES THE SAME INPUT INSTEAD OF ADDING A SCREEN. Three surfaces drive
 * this hook — the global step-up modal, /auth/refresh-step-up, and the trust
 * settings screen — and each renders its own six-digit field wired to
 * `verifyTotp`. Threading a second field through all three would have meant
 * three parallel UIs to keep in step, and any one of them left unwired would
 * have LOCKED OUT an opted-in user on that surface. So the email code is
 * collected through the field that already exists: while `emailCodePending` is
 * true the first submission is read as the EMAIL code, and only afterwards does
 * a submission mean the authenticator. `codeLabel` tells a caller which one is
 * being asked for; a caller that ignores it still works, it just shows a
 * slightly generic label for one step.
 *
 * `showWebAuthnBranch` is suppressed while the email code is outstanding for
 * exactly the same reason — the passkey branch hides the code field, and a
 * passkey user with no field to type into would have had no way forward.
 *
 * Delivery failure fails OPEN (the user proceeds on the factor they hold) and
 * says so via `notice`; a WRONG code never does.
 */

import { useEffect, useState } from 'react';
import { isWebAuthnSupported, stepUpWithPasskey, listPasskeys } from './webauthn';
import { supabase } from './supabase';
import { api, ApiError } from './api';
import { actingContext } from './acting/context';
import { isEmailCodeEnabled, sendEmailCode, verifyEmailCode } from './mfa-email-code';

/** Shape mirroring `AuthMFAListFactorsResponse` items the SDK returns. */
export interface StepUpFactor {
  id: string;
  factor_type: 'totp' | 'phone' | 'webauthn';
  friendly_name?: string;
}

/**
 * The Supabase JS SDK exposes mfa.challenge / mfa.verify with method-specific
 * params. We narrow to the TOTP shape here (factorId + challengeId + code).
 */
interface TotpMfa {
  challenge: (params: { factorId: string }) => Promise<{
    data: { id: string } | null;
    error: Error | null;
  }>;
  verify: (params: { factorId: string; challengeId: string; code: string }) => Promise<{
    data: unknown;
    error: Error | null;
  }>;
}

export interface UseStepUpChallengeResult {
  /** TOTP factor from Supabase, null while loading / none enrolled. */
  factor: StepUpFactor | null;
  /** True when the browser supports WebAuthn AND the user has a passkey AND no
   *  extra email code is outstanding (that branch hides the code field). */
  showWebAuthnBranch: boolean;
  verifying: boolean;
  error: string | null;
  clearError: () => void;
  /**
   * The next code being asked for. While the extra email code is outstanding
   * `verifyTotp` reads its argument as the EMAILED code; afterwards it means the
   * authenticator. Callers that render their own static label still work.
   */
  codeLabel: string;
  /** True while the extra email code is outstanding for this ceremony. */
  emailCodePending: boolean;
  /** Non-error disclosure — e.g. the email code could not be delivered, so the
   *  layer was skipped. Callers should surface it; it must never be silent. */
  notice: string | null;
  /** TOTP path: challenge + verify + backend stamp + session refresh. Reads its
   *  argument as the emailed code while `emailCodePending` is true. */
  verifyTotp: (code: string) => Promise<boolean>;
  /** Passkey path: WebAuthn assert (self-hosted) + session refresh. */
  verifyPasskey: () => Promise<boolean>;
}

const AUTHENTICATOR_CODE_LABEL = 'Authentication code from your app';
const EMAIL_CODE_LABEL = 'Code from your email';

/**
 * Refresh the Supabase session so a freshly-stamped app_metadata.mfa_challenge_at
 * lands in a NEW access token. MANDATORY after any step-up stamp — the api()
 * client resolves the token per call via getSession(), so callers just await
 * this before retrying anything.
 */
async function refreshSessionOrThrow(): Promise<void> {
  const { error: refreshErr } = await supabase.auth.refreshSession();
  if (refreshErr) {
    throw new Error(`Session refresh failed: ${refreshErr.message}`);
  }
}

/**
 * TOTP stamp helper: tell the backend to set app_metadata.mfa_challenge_at (from
 * a fresh Supabase aal2 session), then refresh the session. Shared by the TOTP
 * path AND the onboarding MFA-enrol step, so a user who JUST enrolled TOTP isn't
 * re-prompted on their first confirm.
 *
 * ⛔ WHY THIS PINS TO THE OPERATOR SESSION (ARC-01 tranche T2).
 *
 * Until 2026-08-27 this line was a bare `api(...)` with no audience pin, and
 * that made the TOTP ceremony IMPOSSIBLE to complete while a firm was
 * administering a client. `api()` sends the ambient token, so under act-as the
 * POST travelled on the minted act-as JWT; `backend/src/functions/auth/
 * refresh-step-up.ts:168-177` requires `aal === 'aal2'`, `mintFirmActToken`
 * never stamps an `aal` (or an `mfa_challenge_at`), and the call came back 403
 * MFA_NOT_VERIFIED every time. The user reads that as "it is asking for the
 * wrong person's authenticator", because the code they typed was correct and the
 * ceremony still refused them.
 *
 * The other two factor paths already pinned: `lib/webauthn.ts:82,88` and
 * `lib/mfa-email-code.ts:172,230`. One of three was missed, and it was the one
 * almost every user hits.
 *
 * THE GATE IS THE CREDENTIAL, NOT A HAND-ROLLED "AM I ACTING AS" CHECK. The fact
 * that matters is `credential.canSatisfyStepUp` — false for every MINTED token
 * (act-as and impersonation alike) because neither mint stamps
 * `mfa_challenge_at`. Writing the condition by hand here is exactly how the
 * third path got missed in the first place: a check spelled `firmActStatus() ===
 * 'active'` would have fixed act-as and left impersonation broken in the same
 * way. See `lib/acting/context.ts`.
 *
 * AN EXPIRED MINTED TOKEN ALSO PINS, AND THAT IS DELIBERATE. Step-up freshness
 * is stamped on the OPERATOR's own Supabase user metadata; it is not a
 * tenant-scoped data write, so there is no wrong-tenant write to protect against
 * and no reason to refuse. The operator completes the ceremony, and the replayed
 * data call is what surfaces ACT_AS_EXPIRED and sends them back to the Client
 * Book. Refusing here instead would strand them mid-ceremony with nothing to do.
 *
 * COST: one extra `getSession()` per ceremony (in-memory unless the token is due
 * a refresh), paid at most a handful of times in a session.
 */
export async function stampStepUpFreshness(): Promise<void> {
  const ctx = await actingContext();
  await api('/api/v1/auth/refresh-step-up', {
    method: 'POST',
    useOperatorSession: !ctx.credential.canSatisfyStepUp,
  });
  await refreshSessionOrThrow();
}

export function useStepUpChallenge(): UseStepUpChallengeResult {
  const [factor, setFactor] = useState<StepUpFactor | null>(null);
  const [supportsWebAuthn, setSupportsWebAuthn] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Extra email code. `false` until we know otherwise, so a user without the
  // opt-in — the overwhelming majority — never sees a changed surface.
  const [emailCodePending, setEmailCodePending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setSupportsWebAuthn(isWebAuthnSupported());
  }, []);

  // Arm the extra email code BEFORE either factor path can run. The backend
  // throttles resends per user, so a modal that mounts repeatedly does not
  // become a mailbox flood.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!(await isEmailCodeEnabled())) return;
      if (cancelled) return;
      setEmailCodePending(true);
      const outcome = await sendEmailCode('step-up');
      if (cancelled) return;
      if (outcome.status === 'unavailable') {
        // Fail OPEN — never hold a confirmation hostage to email delivery — but
        // disclose it rather than dropping the control silently.
        setEmailCodePending(false);
        setNotice(`${outcome.reason} Continuing with your authenticator or passkey.`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Consume the emailed code. Returns true when the caller should fall through
   *  to the real factor path, false when it should keep its UI open. */
  async function consumeEmailCode(code: string): Promise<boolean> {
    setError(null);
    setVerifying(true);
    try {
      const outcome = await verifyEmailCode('step-up', code);
      if (outcome.status === 'ok') {
        setEmailCodePending(false);
        // Not an error — but every consumer already renders `error` in a
        // neutral box, and this is the one string that has to reach the user
        // between the two codes.
        setError('Email code accepted. Now enter the code from your authenticator app.');
        return false;
      }
      if (outcome.status === 'unavailable') {
        setEmailCodePending(false);
        setNotice(`${outcome.reason} Continuing with your authenticator or passkey.`);
        setError(null);
        return false;
      }
      // 'invalid' and 'retry' both keep the user on the email step. Neither is
      // ever a pass — that distinction is the whole safety argument.
      setError(outcome.message);
      return false;
    } finally {
      setVerifying(false);
    }
  }

  // TOTP factor discovery (Supabase) — unchanged.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: lfErr } = await supabase.auth.mfa.listFactors();
        if (lfErr) return;
        if (cancelled) return;
        const all = [...(data?.all ?? [])] as StepUpFactor[];
        const totp = all.find((f) => f.factor_type === 'totp');
        setFactor(totp ?? null);
      } catch {
        // Silent — fall through to OTP default UI.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Passkey discovery — Elreign's OWN backend (Supabase has no WebAuthn factor).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const passkeys = await listPasskeys();
        if (!cancelled) setHasPasskey(passkeys.length > 0);
      } catch {
        // No passkey / feature off → TOTP fallback. Never throw here.
        if (!cancelled) setHasPasskey(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function verifyPasskey(): Promise<boolean> {
    // Belt-and-braces: `showWebAuthnBranch` is already suppressed while the
    // email code is outstanding, so no shipped surface can reach here. A
    // caller that drives verifyPasskey() directly still cannot skip the layer.
    if (emailCodePending) {
      setError('Enter the code we emailed you first, then use your passkey.');
      return false;
    }
    setError(null);
    setVerifying(true);
    try {
      // Self-hosted WebAuthn: the finish-step-up endpoint stamps
      // mfa_challenge_at server-side; we only need to refresh the session so the
      // new claim reaches the next request.
      await stepUpWithPasskey();
      await refreshSessionOrThrow();
      return true;
    } catch (e: unknown) {
      setError(prettyStepUpError(e, 'Passkey verification failed.'));
      return false;
    } finally {
      setVerifying(false);
    }
  }

  async function verifyTotp(code: string): Promise<boolean> {
    // The extra email code is consumed through this same call, BEFORE anything
    // that could promote the session or stamp step-up freshness. Note the
    // `!factor` guard below is deliberately AFTER this branch: a user whose
    // second factor is a passkey has no TOTP factor at all, and must still be
    // able to type their emailed code.
    if (emailCodePending) {
      if (code.length !== 6) return false;
      return consumeEmailCode(code);
    }
    if (!factor || code.length !== 6) return false;
    setError(null);
    setVerifying(true);
    try {
      const mfa = supabase.auth.mfa as unknown as TotpMfa;
      const { data: chal, error: cErr } = await mfa.challenge({ factorId: factor.id });
      if (cErr) throw cErr;
      if (!chal) throw new Error('MFA challenge response was empty.');
      const { error: vErr } = await mfa.verify({
        factorId: factor.id,
        challengeId: chal.id,
        code,
      });
      if (vErr) throw vErr;
      await stampStepUpFreshness();
      return true;
    } catch (e: unknown) {
      setError(prettyStepUpError(e, 'Verification failed.'));
      return false;
    } finally {
      setVerifying(false);
    }
  }

  return {
    factor,
    // Suppressed while the email code is outstanding — the passkey branch hides
    // the code field, and a passkey user with nowhere to type would be stuck.
    showWebAuthnBranch: supportsWebAuthn && hasPasskey && !emailCodePending,
    verifying,
    error,
    clearError: () => setError(null),
    codeLabel: emailCodePending ? EMAIL_CODE_LABEL : AUTHENTICATOR_CODE_LABEL,
    emailCodePending,
    notice,
    verifyTotp,
    verifyPasskey,
  };
}

export function prettyStepUpError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.status === 403) {
      return 'The MFA challenge was not fresh enough. Please try again.';
    }
    if (e.status === 429) {
      return 'You are re-challenging too frequently. Wait a minute and try again.';
    }
    return e.message || fallback;
  }
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}
